import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { createCustomerApi } from '@/services/customers'
import { formatDocumentError } from '@/utils/documentErrorMessages'

export function CustomerFormPage() {
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmedCode = code.trim().toUpperCase()
    const trimmedName = name.trim()

    if (!trimmedCode) {
      setErrorMsg('Mã khách hàng không được để trống.')
      return
    }
    if (!trimmedName) {
      setErrorMsg('Tên khách hàng không được để trống.')
      return
    }

    setSubmitting(true)
    setErrorMsg(null)
    try {
      const created = await createCustomerApi({
        code: trimmedCode,
        name: trimmedName,
        note: note.trim() || null,
      })
      navigate(`/sales/customers/${created.id}`)
    } catch (err: unknown) {
      const formatted = formatDocumentError(err)
      setErrorMsg(formatted.message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Sale', to: '/sales' },
          { label: 'Khách hàng', to: '/sales/customers' },
          { label: 'Tạo khách hàng mới' },
        ]}
      />
      <PageHeader
        title="Tạo khách hàng mới"
        subtitle="Khai báo mã, tên và thông tin khách hàng doanh nghiệp"
      />

      {errorMsg && (
        <div className="rounded-[var(--radius-md)] border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <p className="font-semibold">Lỗi thao tác:</p>
          <p>{errorMsg}</p>
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-6 shadow-[var(--shadow-level-1)] space-y-5"
      >
        <div>
          <label className="block text-sm font-medium text-slate-700">
            Mã khách hàng <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="VD: KH-ACME"
            className="mt-1 block w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm font-mono uppercase focus:border-[var(--color-accent)] focus:outline-none"
          />
          <p className="mt-1 text-xs text-slate-500">
            Mã định danh duy nhất (sẽ tự động viết hoa).
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">
            Tên khách hàng <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="VD: Công ty TNHH Nội Thất ACME"
            className="mt-1 block w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">Ghi chú</label>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ghi chú thêm về điều khoản, liên hệ, hoặc địa chỉ..."
            className="mt-1 block w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm focus:border-[var(--color-accent)] focus:outline-none"
          />
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-100 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/sales/customers')}
            disabled={submitting}
          >
            Hủy
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Đang lưu...' : 'Lưu khách hàng'}
          </Button>
        </div>
      </form>
    </div>
  )
}
